import * as Helpers from "./helpers/Calculations";
import useSavingProposal from "@portal/hooks/robot/useSavingProposal";
import Typography from "@portal/components/atoms/v2/typography";
import { MainBox } from "../components/mainBox";
import { ExpectedValueChart } from "./helpers/ExpectedValueChart";
import { IPageProps } from "@portal/types/robot/IPageProps";
import { RobotTextResources } from "@portal/assets/text/RobotTextResources";
import { RobotFooterComponent } from "../components/footer";
import { TextLine } from "@portal/components/atoms/v2/textLine";
import { InfoBoxComponent } from "@portal/components/atoms/infoBox";
import Link from "next/link";
import { FeesOverview } from "./components/fees";
import { OngoingCosts } from "./components/ongoingCosts";
import { useRobot } from "@portal/app/context/robotProvider";
import { useCreateSavingsAgreement } from "@portal/app/context/createSavingsAgreementProvider";
import { EffectCosts } from "./components/effectCosts";
import { useEffect } from "react";

export const ExpectedValuePage = ({ centerId, pageIndex, isDefaultLanguage }: IPageProps) => {
  const { savingProposalRequest: request, setSummedFee } = useRobot();
  const savingProposal = useSavingProposal({ ...request, centerId });
  const { setLoading, clearHasError } = useCreateSavingsAgreement();
  const texts = isDefaultLanguage ? RobotTextResources.primary : RobotTextResources.secondary;
  const isButtonDisabled = savingProposal.error !== undefined && savingProposal.error !== null;

  const proposalResponse = savingProposal.data;
  const dates = Helpers.GetDatesForChart(proposalResponse?.calculations);

  const refundProvisionAmounts = Helpers.GetRefundProvisionAmounts(proposalResponse?.calculations);
  const managmentFees = Helpers.GetManagementFees(proposalResponse?.calculations);
  const managmentFeeAfterTenYears = Helpers.GetManagementFeeForDecade(managmentFees[2], refundProvisionAmounts[2]);
  const managmentFeeAfterOneYear = Helpers.GetManagementFeeForDecade(managmentFees[0], refundProvisionAmounts[0]);
  const depotFees = Helpers.GetDepotFees(proposalResponse?.calculations);
  const depotFeeAfterOneYear = depotFees[0] ?? 0;
  const depotFeeAfterTenYears = depotFees[2] ?? 0;

  const portfolioValues = Helpers.GetPortfolioValues(proposalResponse?.calculations);
  const totalValueWithFees = Helpers.GetTotalValuesWithFees(proposalResponse?.calculations);

  const portfolioValueAfterOneYear = portfolioValues[0] ?? 0;
  const portfolioValueAfterTenYears = portfolioValues[2] ?? 0;
  const totalCostAfterOneYear = managmentFeeAfterOneYear + depotFeeAfterOneYear;
  const totalCostAfterTenYears = managmentFeeAfterTenYears + depotFeeAfterTenYears;
  const totalValueAfterCostOneYear = totalValueWithFees[0] ?? 0;
  const totalValueAfterCost = totalValueWithFees[2] ?? 0;

  const returnsWithoutFee = proposalResponse?.returnsWithoutFeePercentage;
  const returnsWithFee = Helpers.GetReturnsWithFee(proposalResponse?.returnsWithoutFeePercentage, proposalResponse?.summedFee);
  const showReturns = returnsWithFee !== null && returnsWithoutFee !== null && returnsWithoutFee !== undefined;

  useEffect(() => {
    setSummedFee(proposalResponse?.summedFee);
  }, [proposalResponse?.summedFee, setSummedFee]);

  return (
    <>
      <MainBox>
        {showReturns && (
          <Typography variant="H6">
            {`${texts.PAGE_15_EXPECTED_PROFIT}
      ${returnsWithoutFee.toFixed(2)}${texts.PAGE_15_PERCENTAGE_SIGN} ${texts.PAGE_15_BEFORE_COST}
      ${returnsWithFee.toFixed(2)}${texts.PAGE_15_PERCENTAGE_SIGN} ${texts.PAGE_15_AFTER_COST}`}
          </Typography>
        )}
        <TextLine />

        <div className="flex flex-col my-3 border dark:border-blueBorderDark rounded-md w-full p-1 bg-pageBoxBackgroundLight dark:bg-pageBoxBackgroundDark">
          <ExpectedValueChart
            managmentFees={managmentFees}
            depotFees={depotFees}
            portfolioValues={portfolioValues}
            dates={dates}
            isDefaultLanguage={isDefaultLanguage}
          />
        </div>
        <div className="flex flex-col mt-2 space-y-5 md:space-y-0 md:flex-row md:space-x-5">
          <div className="w-full space-y-5">
            <EffectCosts
              title={texts.PAGE_15_ANNUAL}
              isDefaultLanguage={isDefaultLanguage}
              totalValueAfterCost={totalValueAfterCostOneYear}
              portfolioValue={portfolioValueAfterOneYear}
            />
            <OngoingCosts
              title={texts.PAGE_15_ANNUAL_ONGOING_COSTS}
              isDefaultLanguage={isDefaultLanguage}
              managementFee={managmentFeeAfterOneYear}
              depotFee={depotFeeAfterOneYear}
              summedFee={totalCostAfterOneYear}
            />
            <FeesOverview
              isDefaultLanguage={isDefaultLanguage}
              managmentFee={Helpers.GetManagementFee(proposalResponse?.summedFee, proposalResponse?.depotFee)}
              depotFee={proposalResponse?.depotFee}
              summedFee={proposalResponse?.summedFee}
            />
          </div>
          <div className="w-full">
            <EffectCosts
              title={texts.PAGE_15_DECADE}
              isDefaultLanguage={isDefaultLanguage}
              totalValueAfterCost={totalValueAfterCost}
              portfolioValue={portfolioValueAfterTenYears}
            />
            <OngoingCosts
              title={texts.PAGE_15_DECADE_ONGOING_COSTS}
              isDefaultLanguage={isDefaultLanguage}
              managementFee={managmentFeeAfterTenYears}
              depotFee={depotFeeAfterTenYears}
              summedFee={totalCostAfterTenYears}
            />
          </div>
        </div>
        <div className="my-3">
          <Typography variant="Body">
            {texts.PAGE_15_FINANCE_PORTAL_PRE_TEXT}
            <Link href="https://www.forbrukerradet.no/fond" target="_blank" className="text-blueHighlighted">
              {texts.PAGE_15_FINANCE_PORTAL_LINK_TEXT}
            </Link>
          </Typography>
        </div>
        {isButtonDisabled && <InfoBoxComponent texts={[texts.ERROR_FETCH]} warning />}
      </MainBox>
      <RobotFooterComponent
        pageIndex={pageIndex}
        centerId={centerId}
        primaryButtonText={texts.BUTTON_NEXT}
        secondaryButtonText={texts.BUTTON_BACK}
        disabled={isButtonDisabled}
        onNextClick={async () => {
          setLoading(false);
          clearHasError();
        }}
      />
    </>
  );
};
